import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, ArrowLeft, Building2, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../lib/api';
import useAuthStore from '../stores/authStore';
import { TEST_IDS } from '../constants/testIds';

/* ─── Data ─── */
const COMPANIES = [
  'Google', 'Microsoft', 'Amazon', 'Flipkart', 'Samsung R&D',
  'TCS', 'Infosys', 'Wipro', 'Cognizant', 'Capgemini',
];

const TOPICS = [
  'DSA', 'Operating Systems', 'DBMS', 'Computer Networks',
  'System Design', 'OOPs', 'HR / Behavioural',
];

const HR_SUBCATEGORIES = [
  'Personal Introduction and Background',
  'Family and Personal Life',
  'Strengths and Weaknesses',
  'Career Goals and Ambitions',
  'Situational and Behavioural',
  'Teamwork and Collaboration',
  'Leadership and Initiative',
  'Conflict Resolution',
  'Motivation and Work Ethic',
  'Company and Role Fit',
];

const DIFFICULTIES = ['EASY', 'MEDIUM', 'HARD', 'MIXED'];

const difficultyColors = {
  EASY: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
  MEDIUM: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
  HARD: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
  MIXED: 'bg-violet-100 text-[#3D1F6E] border-violet-200 dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-800',
};

export default function InterviewSetup() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [mode, setMode] = useState(null); // 'company' | 'topic'
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedHrSub, setSelectedHrSub] = useState(null);
  const [difficulty, setDifficulty] = useState('MEDIUM');
  const [showHrSubs, setShowHrSubs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Launched from Taskverse's "My Interviews" for a specific assessment —
  // restricts the company/topic choices below to what the trainer configured.
  const [assessmentId] = useState(() => sessionStorage.getItem('botview_assessment_id'));
  const [assessmentConfig, setAssessmentConfig] = useState(null);
  const [assessmentLoading, setAssessmentLoading] = useState(!!assessmentId);
  const [assessmentError, setAssessmentError] = useState(null);

  useEffect(() => {
    if (!assessmentId) return;
    (async () => {
      try {
        const { data } = await api.get(`/assessments/${assessmentId}/interview-config`);
        if (data.already_attempted) {
          // Terminal outcome — this assessment is done, so stop re-applying its
          // restriction and fall back to normal free-practice mode from here on.
          sessionStorage.removeItem('botview_assessment_id');
          setAssessmentError('You have already completed this interview.');
        } else if (!data.is_open) {
          // Not terminal — the window may open shortly, keep the pointer around
          // so re-visiting this page (e.g. via Back to Dashboard) re-checks it.
          const endsAt = data.end_datetime ? new Date(data.end_datetime) : null;
          const hasEnded = endsAt && endsAt.getTime() < Date.now();
          setAssessmentError(
            hasEnded
              ? 'This interview window has closed.'
              : 'This interview is not currently open yet. Please come back during its scheduled time.'
          );
        } else {
          setAssessmentConfig(data);
        }
      } catch (err) {
        // Only give up permanently on a definitive "doesn't exist / not yours"
        // response — a transient failure (network blip, backend hiccup) should
        // still let the student retry with the same restriction intact.
        if (err?.response?.status === 404) {
          sessionStorage.removeItem('botview_assessment_id');
        }
        setAssessmentError(err?.response?.data?.detail || 'Could not load this interview. Please try again.');
      } finally {
        setAssessmentLoading(false);
      }
    })();
  }, [assessmentId]);

  const availableCompanies = assessmentConfig
    ? COMPANIES.filter((c) => assessmentConfig.allowed_companies.includes(c))
    : COMPANIES;
  const availableTopics = assessmentConfig
    ? TOPICS.filter((t) => assessmentConfig.allowed_topics.includes(t))
    : TOPICS;
  // null allowed_difficulty = assessment predates this restriction — keep
  // free choice for it, same as today.
  const availableDifficulties = assessmentConfig?.allowed_difficulty
    ? DIFFICULTIES.filter((d) => d === assessmentConfig.allowed_difficulty)
    : DIFFICULTIES;

  // A restricted assessment only ever leaves one difficulty visible — select
  // it automatically so the student isn't forced to click a single-option row.
  useEffect(() => {
    if (assessmentConfig?.allowed_difficulty) {
      setDifficulty(assessmentConfig.allowed_difficulty);
    }
  }, [assessmentConfig]);

  const canStart =
    difficulty &&
    ((mode === 'company' && selectedCompany) ||
     (mode === 'topic' && selectedTopic));

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic);
    setSelectedHrSub(null);
    setShowHrSubs(topic === 'HR / Behavioural');
  };

  const handleStart = useCallback(async () => {
    if (!canStart) return;
    setLoading(true);
    setError(null);
    try {
      const company = mode === 'company' ? selectedCompany : '';
      const topic = mode === 'topic'
        ? (selectedTopic === 'HR / Behavioural' && selectedHrSub ? selectedHrSub : selectedTopic)
        : '';
      const studentId = user.user_id;

      let questions = [];
      try {
        // Exclude questions this student has already been asked
        const { data: seenData } = await api.get(`/students/${studentId}/seen-questions`);
        const excludeIds = seenData.question_ids || [];

        // Semantic search for a fresh, relevant question set
        const { data: searchData } = await api.post('/questions/semantic-search', {
          topic,
          company,
          difficulty,
          student_id: studentId,
          exclude_ids: excludeIds,
        });
        questions = searchData.questions || [];
      } catch (fetchErr) {
        console.error('Failed to load questions:', fetchErr);
        setError('Could not load questions, please try again.');
        setLoading(false);
        return;
      }

      const payload = {
        mode,
        difficulty,
        company: company || null,
        topic: topic || null,
        questions,
        assessment_id: assessmentConfig ? assessmentId : null,
      };
      const { data } = await api.post('/sessions', payload);
      // Deliberately not clearing 'botview_assessment_id' here — starting a
      // session isn't a terminal outcome (the student might end early and
      // retry). The next InterviewSetup mount re-checks the server-side
      // already_attempted/is_open state and clears the pointer then, once
      // the outcome is actually final.
      navigate(`/interview/${data.session_id}`);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to start interview');
    } finally {
      setLoading(false);
    }
  }, [canStart, mode, difficulty, selectedCompany, selectedTopic, selectedHrSub, user, navigate, assessmentConfig, assessmentId]);

  if (assessmentLoading) {
    return (
      <div className="max-w-3xl mx-auto py-16 text-center text-sm text-muted-foreground" data-testid="interview-setup-page">
        Loading your interview...
      </div>
    );
  }

  if (assessmentError) {
    return (
      <div className="max-w-3xl mx-auto py-16 text-center space-y-4" data-testid="interview-setup-page">
        <p className="text-base font-semibold font-heading">{assessmentError}</p>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1.5 text-sm text-[#3D1F6E] dark:text-[#A78BFA] hover:underline"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8 py-4" data-testid="interview-setup-page">
      {/* Header */}
      <div className="space-y-1">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
        <p className="text-[10px] font-heading uppercase tracking-[0.2em] text-[#3D1F6E] dark:text-[#A78BFA] font-semibold">
          {assessmentConfig ? assessmentConfig.assessment_name : 'New Session'}
        </p>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-heading">
          Interview Setup
        </h1>
        <p className="text-sm text-muted-foreground">
          Choose your interview mode, topic, and difficulty level
        </p>
      </div>

      {/* Step 1: Mode Selection */}
      <div className="space-y-3">
        <h2 className="text-sm font-heading font-semibold text-[#3D1F6E] dark:text-[#A78BFA]">
          Step 1 — Choose Mode
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {availableCompanies.length > 0 && (
            <ModeCard
              icon={Building2}
              title="Company Mode"
              desc="Practice for a specific company's interview style"
              active={mode === 'company'}
              onClick={() => { setMode('company'); setSelectedTopic(null); setSelectedHrSub(null); setShowHrSubs(false); }}
              testId="mode-company-btn"
            />
          )}
          {availableTopics.length > 0 && (
            <ModeCard
              icon={BookOpen}
              title="Topic Mode"
              desc="Focus on a specific technical or HR topic"
              active={mode === 'topic'}
              onClick={() => { setMode('topic'); setSelectedCompany(null); }}
              testId="mode-topic-btn"
            />
          )}
        </div>
      </div>

      {/* Step 2: Selection */}
      {mode && (
        <div className="space-y-3 anim-fade-up">
          <h2 className="text-sm font-heading font-semibold text-[#3D1F6E] dark:text-[#A78BFA]">
            Step 2 — {mode === 'company' ? 'Select Company' : 'Select Topic'}
          </h2>

          {mode === 'company' && (
            <div className="flex flex-col gap-2.5 stagger" data-testid="company-chips">
              {availableCompanies.map((c) => (
                <Chip
                  key={c}
                  label={c}
                  active={selectedCompany === c}
                  onClick={() => setSelectedCompany(c)}
                  testId={`company-chip-${c.toLowerCase().replace(/\s+/g, '-')}`}
                />
              ))}
            </div>
          )}

          {mode === 'topic' && (
            <>
              <div className="flex flex-col gap-2.5 stagger" data-testid="topic-chips">
                {availableTopics.map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    active={selectedTopic === t}
                    onClick={() => handleTopicSelect(t)}
                    testId={`topic-chip-${t.toLowerCase().replace(/[\s\/]+/g, '-')}`}
                  />
                ))}
              </div>

              {/* HR Subcategories */}
              {selectedTopic === 'HR / Behavioural' && (
                <div className="space-y-2 mt-3 anim-fade-up">
                  <button
                    onClick={() => setShowHrSubs(!showHrSubs)}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="toggle-hr-subs-btn"
                  >
                    {showHrSubs ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    {showHrSubs ? 'Hide' : 'Show'} subcategories (optional)
                  </button>
                  {showHrSubs && (
                    <div className="flex flex-col gap-2 anim-fade-up" data-testid="hr-sub-chips">
                      {HR_SUBCATEGORIES.map((s) => (
                        <Chip
                          key={s}
                          label={s}
                          active={selectedHrSub === s}
                          onClick={() => setSelectedHrSub(selectedHrSub === s ? null : s)}
                          small
                          testId={`hr-sub-chip-${s.toLowerCase().replace(/\s+/g, '-')}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Step 3: Difficulty */}
      {mode && (selectedCompany || selectedTopic) && (
        <div className="space-y-3 anim-fade-up">
          <h2 className="text-sm font-heading font-semibold text-[#3D1F6E] dark:text-[#A78BFA]">
            Step 3 — Difficulty
          </h2>
          <div className="flex flex-wrap gap-2" data-testid="difficulty-chips">
            {availableDifficulties.map((d) => (
              <button
                key={d}
                onClick={() => setDifficulty(d)}
                data-testid={`difficulty-chip-${d.toLowerCase()}`}
                className={`px-4 py-2 rounded-lg text-sm font-semibold font-heading border transition-all
                  ${difficulty === d
                    ? difficultyColors[d]
                    : 'bg-secondary/40 text-muted-foreground border-border hover:bg-secondary'
                  }`}
              >
                {d === 'MIXED' ? 'Mixed' : d.charAt(0) + d.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Summary + Start */}
      {canStart && (
        <Card className="bg-card border-border card-shadow blob-mixed anim-fade-up">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground font-heading uppercase tracking-wider">Ready to start</p>
                <p className="text-base font-semibold font-heading">
                  {mode === 'company' ? selectedCompany : selectedTopic}
                  {selectedHrSub && <span className="text-muted-foreground font-normal"> — {selectedHrSub}</span>}
                </p>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-[10px] font-heading capitalize">
                    {mode} mode
                  </Badge>
                  <Badge
                    className={`text-[10px] font-heading border ${difficultyColors[difficulty]}`}
                    variant="outline"
                  >
                    {difficulty}
                  </Badge>
                </div>
              </div>
              <button
                onClick={handleStart}
                disabled={loading}
                data-testid="start-interview-confirm-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold font-heading btn-cta disabled:opacity-50"
              >
                {loading ? 'Starting...' : 'Start Interview'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            {error && (
              <div
                className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/10 px-3 py-2"
                data-testid="setup-error"
              >
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                <button
                  onClick={handleStart}
                  disabled={loading}
                  data-testid="setup-error-retry-btn"
                  className="shrink-0 text-xs font-semibold font-heading text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                >
                  Retry
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ─── Sub-components ─── */

function ModeCard({ icon: Icon, title, desc, active, onClick, testId }) {
  return (
    <Card
      onClick={onClick}
      data-testid={testId}
      className={`cursor-pointer transition-all card-shadow blob-violet hover:-translate-y-0.5
        ${active
          ? 'border-[#3D1F6E] dark:border-[#A78BFA] ring-1 ring-[#3D1F6E]/20 dark:ring-[#A78BFA]/20 bg-card'
          : 'border-border bg-card hover:border-[#3D1F6E]/30 dark:hover:border-[#A78BFA]/30'
        }`}
    >
      <CardContent className="p-5 flex items-start gap-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0
          ${active ? 'bg-[#3D1F6E] text-white' : 'icon-bg-violet'}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm font-semibold font-heading">{title}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function Chip({ label, active, onClick, small, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`tech-chip anim-fade-up ${small ? 'tech-chip-sm' : ''} ${active ? 'tech-chip-active' : ''}`}
    >
      {label}
    </button>
  );
}
